import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { TenantUserSettings } from '@/api/generatedApi';
import { PARAM_NAMES, useSearchFilters } from '@/hooks/useSearchFilters';

export const useSearchTabs = () => {
  const { currentTenant, tenants: unsortedTenants } = useGetCurrentUser();

  const { selectedTenantId } = useSearchFilters();
  const [searchParameters, setSearchParameters] = useSearchParams();

  const searchTabs = useMemo(() => {
    return [...unsortedTenants].sort((a, b) => {
      const currentTenantId = currentTenant?.tenantId;
      if (a?.tenantId === currentTenantId) return -1;
      if (b?.tenantId === currentTenantId) return 1;
      return 0;
    });
  }, [unsortedTenants, currentTenant]);

  const shouldShowSearchTabs = searchTabs.length > 1;

  const selectedSearchTab = useMemo(() => {
    if (selectedTenantId) {
      const foundTenant = searchTabs.find(tenant => tenant?.tenantId === selectedTenantId);
      if (foundTenant) return foundTenant;
    }
    return searchTabs[0];
  }, [selectedTenantId, searchTabs]);

  const handleSelectSearchTab = (tenant?: TenantUserSettings | null) => {
    if (shouldShowSearchTabs) {
      const updatedParameters = new URLSearchParams(searchParameters);
      if (tenant?.tenantShortName) {
        updatedParameters.set(PARAM_NAMES.TENANT, tenant.tenantShortName);
      } else {
        updatedParameters.delete(PARAM_NAMES.TENANT);
      }
      setSearchParameters(updatedParameters);
    }
  };

  const clearSearchTabSelection = () => {
    const updatedParameters = new URLSearchParams(searchParameters);
    updatedParameters.delete(PARAM_NAMES.TENANT);
    setSearchParameters(updatedParameters);
  };

  useEffect(() => {
    if (selectedSearchTab?.tenantId !== selectedTenantId) {
      handleSelectSearchTab(selectedSearchTab);
    }
  }, [selectedTenantId]);

  return {
    searchTabs,
    selectedSearchTab,
    handleSelectSearchTab,
    clearSearchTabSelection,
    shouldShowSearchTabs,
  };
};
